import React, { useEffect } from "react";

export function AboutPage() {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add("is-visible");
        });
      },
      { threshold: 0.1 },
    );
    document
      .querySelectorAll(".animate-on-scroll, .stat-card")
      .forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="page-content">
      <section className="hero is-medium about-hero">
        <div className="hero-body">
          <div className="container has-text-centered">
            <p className="about-hero-label">WHO WE ARE</p>
            <h1
              className="title is-1 has-text-white"
              style={{
                fontSize: "2.5rem",
                fontWeight: 700,
                letterSpacing: "2px",
              }}
            >
              Building the pricing infrastructure
              <br />
              that esports betting{" "}
              <span style={{ color: "#6ee7b7" }}>deserves</span>
            </h1>
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="section about-section">
        <div className="container">
          <div className="columns is-centered">
            <div className="column is-8">
              <h2 className="title is-3 has-text-white has-text-centered">
                Our Mission
              </h2>
              <p
                className="is-size-5 has-text-centered"
                style={{ color: "#c4c4c4" }}
              >
                Esports is one of the fastest-growing entertainment industries
                in the world, yet the betting infrastructure behind it remains
                years behind traditional sports. We're here to change that.
              </p>
              <p
                className="is-size-5 has-text-centered mt-4"
                style={{ color: "#c4c4c4" }}
              >
                Sitech eSports exists to give betting operators the same quality
                of pricing for esports that they expect from football, tennis,
                or basketball — automated, data-driven, and delivered in real
                time.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Story */}
      <section className="section" style={{ backgroundColor: "#252535" }}>
        <div className="container">
          <div className="columns is-vcentered">
            <div className="column is-6">
              <h2 className="title is-3 has-text-white">The Problem We Saw</h2>
              <div className="content" style={{ color: "#c4c4c4" }}>
                <p>
                  Most sportsbooks either don't offer esports markets, or price
                  them manually with small trading teams who can't scale. The
                  result? Limited market coverage, slow updates, and pricing
                  that doesn't reflect the true complexity of competitive
                  gaming.
                </p>
                <p>
                  Esports isn't like traditional sports. Map vetoes, game mode
                  rotations, round economics, and roster instability all create
                  pricing challenges that manual traders struggle with. We saw
                  an opportunity to solve this with technology.
                </p>
              </div>
            </div>
            <div className="column is-6">
              <h2 className="title is-3 has-text-white">What We Built</h2>
              <div className="content" style={{ color: "#c4c4c4" }}>
                <p>
                  A simulation engine that models esports matches from the
                  ground up. 100,000 Monte Carlo iterations per fixture.
                  Game-specific mechanics baked into every calculation.
                  Opposition-adjusted player stats. LAN vs online weighting.
                  Recency-weighted form.
                </p>
                <p>
                  The result is a pricing API that produces accurate
                  probabilities across series winner, correct score, map winner,
                  over/under lines, and bet builder markets — all delivered in
                  sub-second via an event-driven architecture.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="section about-section">
        <div className="container">
          <h2 className="title is-3 has-text-white has-text-centered mb-6">
            What Drives Us
          </h2>
          <div className="columns is-multiline">
            <div className="column is-4">
              <div className="has-text-centered">
                <i
                  className="fa-solid fa-bullseye fa-2x mb-3"
                  style={{ color: "#E8655A" }}
                ></i>
                <h4 className="title is-5 has-text-white">
                  Accuracy Over Everything
                </h4>
                <p style={{ color: "#c4c4c4" }}>
                  We backtest every model change against real results. If the
                  data says we're wrong, we fix it.
                </p>
              </div>
            </div>
            <div className="column is-4">
              <div className="has-text-centered">
                <i
                  className="fa-solid fa-bolt fa-2x mb-3"
                  style={{ color: "#6ee7b7" }}
                ></i>
                <h4 className="title is-5 has-text-white">Speed Matters</h4>
                <p style={{ color: "#c4c4c4" }}>
                  Sub-second pricing updates. Event-driven distribution.
                  Operators need prices before markets move.
                </p>
              </div>
            </div>
            <div className="column is-4">
              <div className="has-text-centered">
                <i
                  className="fa-solid fa-gamepad fa-2x mb-3"
                  style={{ color: "#E8655A" }}
                ></i>
                <h4 className="title is-5 has-text-white">Esports-Native</h4>
                <p style={{ color: "#c4c4c4" }}>
                  We don't adapt traditional sports models. We build from
                  scratch for the mechanics of each game.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="section" style={{ backgroundColor: "#252535" }}>
        <div className="container">
          <p
            className="has-text-centered mb-2"
            style={{
              color: "#6ee7b7",
              fontSize: "0.9rem",
              letterSpacing: "3px",
              textTransform: "uppercase",
            }}
          >
            Proven Performance
          </p>
          <h2
            className="title is-2 has-text-white has-text-centered mb-6"
            style={{ letterSpacing: "4px" }}
          >
            BY THE NUMBERS
          </h2>
          <div className="columns has-text-centered stat-row animate-on-scroll">
            <div className="column">
              <p className="stat-number has-text-white">100K</p>
              <p className="stat-label">Simulations per fixture</p>
            </div>
            <div className="column">
              <p className="stat-number" style={{ color: "#6ee7b7" }}>
                &lt;1s
              </p>
              <p className="stat-label">Pricing updates</p>
            </div>
            <div className="column">
              <p className="stat-number has-text-white">6+</p>
              <p className="stat-label">Betting markets priced</p>
            </div>
            <div className="column">
              <p className="stat-number" style={{ color: "#E8655A" }}>
                11+
              </p>
              <p className="stat-label">Esports titles on roadmap</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section about-section has-text-centered">
        <div className="container">
          <h2 className="title is-3 has-text-white">Want to work with us?</h2>
          <p className="is-size-5 mb-5" style={{ color: "#c4c4c4" }}>
            Whether you're an operator looking for esports pricing or a
            developer interested in our API, we'd love to hear from you.
          </p>
          <a href="/contact" className="button is-primary is-medium">
            Get In Touch
          </a>
        </div>
      </section>
    </div>
  );
}
